import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useDropzone } from "react-dropzone";
import { Card, CardContent, CardHeader, CardTitle } from "@foundation/components/ui/card";
import { Button } from "@foundation/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@foundation/components/ui/select";
import { Input } from "@foundation/components/ui/input";
import { Label } from "@foundation/components/ui/label";
import { Badge } from "@foundation/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@foundation/components/ui/dialog";
import { Stepper } from "@foundation/components/stepper";
import {
  Upload,
  FileText,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  X,
  AlertCircle,
  Search,
  Loader2,
} from "lucide-react";
import { useToast } from "@foundation/hooks/use-toast";
import { useUser } from "@foundation/contextapi/UserContext";
import { offlineSupabase } from "@foundation/utils/offlineSupabaseProxy";
import { enhancedClientService } from "../services/enhancedClientService";
import {
  createRagStore,
  uploadToRagStore,
  listFilesInStore,
  getFileDetails,
} from "../services/quickCompareService";
import ScopingAILayout from "../components/RevisrLayout";

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: string;
  type: string;
}

interface AuditData {
  oldAudit: {
    file: UploadedFile | null;
    validationType: string;
    dataType: string;
  };
  newAudit: {
    file: UploadedFile | null;
    validationType: string;
    dataType: string;
  };
}

export default function NewAudit() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useUser();

  const [selectedIndustry, setSelectedIndustry] = useState<string>("");
  const [industrySearch, setIndustrySearch] = useState<string>("");
  const [isIndustryOpen, setIsIndustryOpen] = useState<boolean>(false);
  const [documentTypeSearch, setDocumentTypeSearch] = useState<string>("");
  const [isDocumentTypeOpen, setIsDocumentTypeOpen] = useState<boolean>(false);
  const [workflowType, setWorkflowType] = useState<string>("");
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);
  const uploadInProgressRef = React.useRef(false);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; category: 'policy' | 'standard' }>>([]);
  const [selectedPolicyFile, setSelectedPolicyFile] = useState<string | null>(null);
  const [selectedStandardFiles, setSelectedStandardFiles] = useState<string[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isCheckingStore, setIsCheckingStore] = useState(true);
  const hasLoadedFilesRef = React.useRef(false);
  const ragService: 'gemini' | 'langgraph' = 'langgraph'; // Use langgraph for NewAudit
  const [previewDialog, setPreviewDialog] = useState<{
    isOpen: boolean;
    file: File | null;
    fileType: string;
    previewUrl: string | null;
  }>({
    isOpen: false,
    file: null,
    fileType: '',
    previewUrl: null,
  });

  // Helper function to get localStorage key with user email
  const getStoreIdKey = (): string => {
    const userEmail = user?.email || 'anonymous';
    const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    return `quickCompare_storeId_${ragService}_${sanitizedEmail}`;
  };

  // Helper function to get store name with user email
  const getStoreName = (): string => {
    const userEmail = user?.email || 'anonymous';
    const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    return `quick-compare-persistent-${ragService}-${sanitizedEmail}`;
  };

  // Get workflow type on mount
  React.useEffect(() => {
    const workflow = sessionStorage.getItem('workflow_type');
    setWorkflowType(workflow || '');
  }, []);

  // Get orgId using the same approach as QuickCompare
  React.useEffect(() => {
    const getOrgId = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await offlineSupabase
          .rpc("get_user_organizations", { p_user_id: user.id });

        if (error) {
          console.warn('Could not get organizations via RPC:', error);
          try {
            const orgIds = await enhancedClientService.getUserOrganizations();
            if (orgIds && orgIds.length > 0) {
              setOrgId(orgIds[0]);
              return;
            }
          } catch (fallbackErr) {
            console.warn('Fallback method also failed:', fallbackErr);
          }
          return;
        }

        if (data && data.length > 0) {
          setOrgId(data[0].id);
        } else {
          try {
            const orgIds = await enhancedClientService.getUserOrganizations();
            if (orgIds && orgIds.length > 0) {
              setOrgId(orgIds[0]);
            }
          } catch (fallbackErr) {
            console.warn('Fallback method also failed:', fallbackErr);
          }
        }
      } catch (err) {
        console.warn('Could not get orgId:', err);
        try {
          const orgIds = await enhancedClientService.getUserOrganizations();
          if (orgIds && orgIds.length > 0) {
            setOrgId(orgIds[0]);
          }
        } catch (fallbackErr) {
          console.warn('All methods to get orgId failed:', fallbackErr);
        }
      }
    };

    getOrgId();
  }, [user?.id]);

  // Load uploaded files from store
  const loadUploadedFiles = async (storeIdToLoad: string): Promise<Array<{ name: string; category: 'policy' | 'standard' }>> => {
    try {
      setIsLoadingFiles(true);
      console.log('Fetching files with:', { storeId: storeIdToLoad, ragService, userId: user?.id, orgId });
      const files = await listFilesInStore(storeIdToLoad, undefined, ragService, user?.id, orgId);
      console.log('Fetched files:', files);
      setUploadedFiles(files);
      hasLoadedFilesRef.current = true;
      console.log('Loaded uploaded files', { storeId: storeIdToLoad, fileCount: files.length });
      return files;
    } catch (error) {
      console.warn('Could not load uploaded files (server may not be running):', error);
      return [];
    } finally {
      setIsLoadingFiles(false);
    }
  };

  // Handle selecting existing file from store
  const handleSelectExistingFile = (
    fileName: string,
    fileType: 'policy' | 'standard'
  ): void => {
    if (fileType === 'policy') {
      setSelectedPolicyFile(fileName);
      // Clear uploaded file if any
      setAuditData(prev => ({
        ...prev,
        oldAudit: {
          ...prev.oldAudit,
          file: null,
        },
      }));
    } else {
      // Toggle selection for standards
      setSelectedStandardFiles(prev => {
        if (prev.includes(fileName)) {
          return prev.filter(f => f !== fileName);
        } else {
          return [...prev, fileName];
        }
      });
      // Clear uploaded file if any
      setAuditData(prev => ({
        ...prev,
        newAudit: {
          ...prev.newAudit,
          file: null,
        },
      }));
    }

    toast({
      title: 'File Selected',
      description: `Using existing file: ${fileName}. No upload needed.`,
    });
  };

  // Check for existing store and load files on mount
  // UPDATED: Query database directly instead of relying solely on localStorage
  React.useEffect(() => {
    const checkExistingStore = async (): Promise<void> => {
      if (!user || !orgId) {
        setIsCheckingStore(false);
        return;
      }

      // Prevent re-running if we've already loaded files
      if (hasLoadedFilesRef.current) {
        return;
      }

      setIsCheckingStore(true);

      try {
        // NEW: Query database directly for existing files (fixes first load bug)
        const response = await fetch(`/api/revisr/user-documents`, {
          headers: {
            'x-user-id': user.id,
            'x-org-id': orgId,
          },
        });

        if (response.ok) {
          const { files } = await response.json();
          
          if (files && files.length > 0) {
            // Found files in database - group by store_id
            const policyFiles = files.filter((f: any) => f.category === 'policy');
            const standardFiles = files.filter((f: any) => f.category === 'standard');
            
            // Get store IDs for localStorage (for backward compatibility)
            if (policyFiles.length > 0) {
              const policyStoreId = policyFiles[0].store_id;
              localStorage.setItem(`${getStoreIdKey()}_policy`, policyStoreId);
              setStoreId(policyStoreId); // Use policy store as the main storeId
            }
            if (standardFiles.length > 0) {
              const standardStoreId = standardFiles[0].store_id;
              localStorage.setItem(`${getStoreIdKey()}_standard`, standardStoreId);
            }
            
            setUploadedFiles(files.map((f: any) => ({ name: f.name, category: f.category })));
            console.log('✅ Loaded existing files from database:', files.length);
          } else {
            // No files in database - check localStorage as fallback
            const savedStoreId = localStorage.getItem(getStoreIdKey());
            if (savedStoreId) {
              try {
                setStoreId(savedStoreId);
                const localFiles = await loadUploadedFiles(savedStoreId);
                setUploadedFiles(localFiles);
              } catch (err) {
                console.warn('Could not load files from localStorage storeId:', err);
              }
            }
          }
        } else {
          // API not available - fall back to localStorage
          const savedStoreId = localStorage.getItem(getStoreIdKey());
          if (savedStoreId) {
            try {
              setStoreId(savedStoreId);
              const localFiles = await loadUploadedFiles(savedStoreId);
              setUploadedFiles(localFiles);
            } catch (err) {
              console.warn('Could not load files from localStorage storeId:', err);
            }
          }
        }
        
        hasLoadedFilesRef.current = true;
      } catch (error) {
        console.warn('Could not load existing files:', error);
        // Fall back to localStorage approach
        const savedStoreId = localStorage.getItem(getStoreIdKey());
        if (savedStoreId) {
          try {
            setStoreId(savedStoreId);
            const localFiles = await loadUploadedFiles(savedStoreId);
            setUploadedFiles(localFiles);
          } catch (err) {
            console.warn('Fallback also failed:', err);
          }
        }
        hasLoadedFilesRef.current = true;
      } finally {
        setIsCheckingStore(false);
      }
    };

    checkExistingStore();
  }, [user, orgId]);

  // Load files when storeId changes
  React.useEffect(() => {
    if (storeId && uploadedFiles.length === 0 && orgId && !hasLoadedFilesRef.current) {
      loadUploadedFiles(storeId);
    }
  }, [storeId, orgId]);

  const [auditData, setAuditData] = useState<AuditData>({
    oldAudit: {
      file: null,
      validationType: "",
      dataType: "",
    },
    newAudit: {
      file: null,
      validationType: "",
      dataType: "",
    },
  });


  const industries = [
    { value: "Manufacturing", label: "Manufacturing & Engineering" },
    { value: "Financial", label: "Financial Services & Accounting" },
    { value: "Insurance", label: "Insurance & Wealth Management" },
    { value: "Legal", label: "Legal & Professional Services" },
    { value: "Healthcare", label: "Healthcare & Pharmaceuticals" },
    { value: "IT", label: "IT & Cybersecurity" },
    { value: "Construction", label: "Construction, Mining & Energy" },
    { value: "Marketing", label: "Marketing, Advertising & Media" },
    { value: "Education", label: "Education & Training" },
    { value: "Government", label: "Government & Public Sector" },
    { value: "Realestate", label: "Real Estate & Property Management" },
  ];

  const documentTypes = [
    { value: "isomanuals", label: "ISO Manuals" },
    { value: "workinstructions", label: "Work Instructions" },
    { value: "auditchecklists", label: "Audit Checklists" },
    { value: "aasbifrreports", label: "AASB/IFRS Reports" },
    { value: "auditworkpapers", label: "Audit Workpapers" },
    { value: "privacyamlpolicies", label: "Privacy & AML Policies" },
    { value: "policywordings", label: "Policy Wordings" },
    { value: "riskassessmentforms", label: "Risk-Assessment Forms" },
    { value: "claimsprocedures", label: "Claims Procedures" },
    { value: "msas", label: "MSAs" },
    { value: "ndas", label: "NDAs" },
    { value: "engagementletters", label: "Engagement Letters" },
    { value: "clinicalsops", label: "Clinical SOPs" },
    { value: "consentforms", label: "Consent Forms" },
    { value: "privacynotices", label: "Privacy Notices" },
    { value: "iso27001policies", label: "ISO 27001/27017 Policies" },
    { value: "incidentresponseplaybooks", label: "Incident-Response Playbooks" },
    { value: "dataprocessingaddenda", label: "Data-Processing Addenda" },
    { value: "safetycases", label: "Safety Cases" },
    { value: "environmentalassessments", label: "Environmental Assessments" },
    { value: "qaprocedures", label: "QA Procedures" },
    { value: "brandguidelines", label: "Brand Guidelines" },
    { value: "compliancedisclosures", label: "Compliance Disclosures" },
    { value: "curriculumapprovals", label: "Curriculum Approvals" },
    { value: "assessmentrubrics", label: "Assessment Rubrics" },
    { value: "policyframeworks", label: "Policy Frameworks" },
    { value: "compliancereports", label: "Compliance Reports" },
    { value: "leaseagreements", label: "Lease Agreements" },
    { value: "inspectionreports", label: "Inspection Reports" },
  ];

  // Get all document types
  const getAllDocumentTypes = () => {
    return documentTypes.sort((a, b) => a.label.localeCompare(b.label));
  };

  const allDocumentTypes = getAllDocumentTypes();

  // Filter industries based on search
  const filteredIndustries = industries.filter(industry =>
    industry.label.toLowerCase().includes(industrySearch.toLowerCase())
  );

  // Filter document types based on search
  const filteredDocumentTypes = allDocumentTypes.filter(docType =>
    docType.label.toLowerCase().includes(documentTypeSearch.toLowerCase())
  );

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const createFileUploadHandler = (auditType: 'oldAudit' | 'newAudit') => {
    return (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const uploadedFile: UploadedFile = {
          id: Math.random().toString(36).substring(2, 15),
          file,
          name: file.name,
          size: formatFileSize(file.size),
          type: file.type,
        };

        setAuditData(prev => ({
          ...prev,
          [auditType]: {
            ...prev[auditType],
            file: uploadedFile,
          },
        }));

        toast({
          title: "File uploaded successfully",
          description: `${file.name} has been uploaded.`,
        });
      }
    };
  };

  const createDropzoneConfig = (auditType: 'oldAudit' | 'newAudit') => {
    return useDropzone({
      onDrop: createFileUploadHandler(auditType),
      accept: {
        "application/pdf": [".pdf"],
        "application/msword": [".doc"],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
        "text/plain": [".txt"],
      },
      maxFiles: 1,
      maxSize: 50 * 1024 * 1024, // 50MB
      disabled: isUploading,
    });
  };

  const oldAuditDropzone = createDropzoneConfig('oldAudit');
  const newAuditDropzone = createDropzoneConfig('newAudit');

  const removeFile = (auditType: 'oldAudit' | 'newAudit') => {
    setAuditData(prev => ({
      ...prev,
      [auditType]: {
        ...prev[auditType],
        file: null,
      },
    }));
  };

  const handlePreviewFile = (file: File) => {
    const fileType = file.type;
    let previewUrl = null;

    // Create blob URL for file preview
    if (fileType === 'application/pdf' || fileType.startsWith('text/')) {
      previewUrl = URL.createObjectURL(file);
    }

    setPreviewDialog({
      isOpen: true,
      file,
      fileType,
      previewUrl,
    });
  };

  const closePreview = (open?: boolean) => {
    if (open === false || open === undefined) {
      if (previewDialog.previewUrl) {
        URL.revokeObjectURL(previewDialog.previewUrl);
      }
      setPreviewDialog({
        isOpen: false,
        file: null,
        fileType: '',
        previewUrl: null,
      });
    }
  };

  const updateAuditField = (auditType: 'oldAudit' | 'newAudit', field: 'validationType' | 'dataType', value: string) => {
    setAuditData(prev => ({
      ...prev,
      [auditType]: {
        ...prev[auditType],
        [field]: value,
      },
    }));
  };

  const canCompare = () => {
    // Check if files are uploaded OR existing files are selected
    const hasPolicyFile = auditData.oldAudit.file || selectedPolicyFile;
    const hasStandardFile = auditData.newAudit.file || selectedStandardFiles.length > 0;
    return hasPolicyFile && hasStandardFile;
  };

  // const handleCompare = () => {
  //   if (canCompare()) {
  //     // Store audit data in session storage for the next page
  //     sessionStorage.setItem('auditComparisonData', JSON.stringify(auditData));
  //     // Also store selected industry separately
  //     sessionStorage.setItem('selectedIndustry', selectedIndustry);
  //     navigate("/revisr/reference-document");
  //   } else {
  //     toast({
  //       title: "Missing Information",
  //       description: "Please upload a document and choose a document type.",
  //       variant: "destructive",
  //     });
  //   }
  // };

  const handleSelectTemplate = async () => {
    // Prevent duplicate calls
    if (uploadInProgressRef.current || isUploading) {
      console.warn('Upload already in progress, ignoring duplicate call');
      return;
    }

    if (!canCompare()) {
      toast({
        title: "Missing Information",
        description: "Please upload both policy and standard documents.",
        variant: "destructive",
      });
      return;
    }

    if (!orgId) {
      toast({
        title: "Organization Required",
        description: "Unable to determine your organization. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    uploadInProgressRef.current = true;
    setIsUploading(true);

    try {
      // Determine which files to use (uploaded or selected)
      const policyFile = auditData.oldAudit.file?.file as File | undefined;
      const standardFile = auditData.newAudit.file?.file as File | undefined;

      const policyFileName = selectedPolicyFile || policyFile?.name;
      const standardFileName = selectedStandardFiles.length > 0 ? selectedStandardFiles[0] : standardFile?.name;

      if (!policyFileName || !standardFileName) {
        toast({
          title: "Missing Files",
          description: "Please ensure both files are uploaded or selected.",
          variant: "destructive",
        });
        setIsUploading(false);
        return;
      }

      // Use existing store or create new one
      let currentStoreId = storeId || localStorage.getItem(getStoreIdKey());

      if (!currentStoreId) {
        // Create new store
        const storeName = getStoreName();
        currentStoreId = await createRagStore(storeName, ragService, user?.id, orgId);
        localStorage.setItem(getStoreIdKey(), currentStoreId);
        setStoreId(currentStoreId);
        console.log('✅ Created RAG store:', currentStoreId);
      } else {
        setStoreId(currentStoreId);
        console.log('✅ Using existing RAG store:', currentStoreId);
      }

      let policyConversionSource = '';
      let policyMarkdownUrl = '';
      let policyStoreId = '';
      let standardStoreId = '';

      // Upload files only if they were uploaded (not selected from existing files)
      if (policyFile && !selectedPolicyFile) {
        console.log('📤 Uploading policy file:', policyFile.name);

        // Use uploadToRagStore which now handles conversion and returns markdownUrl
        const uploadResult = await uploadToRagStore(currentStoreId, policyFile, 'policy', ragService, user?.id, orgId) as any;
        console.log('✅ Policy file uploaded and converted:', uploadResult);

        if (uploadResult && uploadResult.markdownUrl && uploadResult.content) {
          // Store the markdown URL and content in session storage for EditAndApprove page
          sessionStorage.setItem('policyMarkdownUrl', uploadResult.markdownUrl);
          sessionStorage.setItem('documentContent', uploadResult.content);
          policyConversionSource = uploadResult.conversionSource || '';
          policyMarkdownUrl = uploadResult.markdownUrl;
          policyStoreId = uploadResult.storeId || currentStoreId;
        } else {
          console.warn('⚠️ Policy upload did not return markdown URL or content');
        }
      } else if (selectedPolicyFile) {
        // Fetch file details for existing selected policy file
        console.log('📥 Fetching details for existing policy file:', selectedPolicyFile);
        const fileDetails = await getFileDetails(selectedPolicyFile, 'policy', user?.id, orgId);
        
        if (fileDetails) {
          if (fileDetails.markdownUrl) {
            sessionStorage.setItem('policyMarkdownUrl', fileDetails.markdownUrl);
            policyMarkdownUrl = fileDetails.markdownUrl;
          }
          if (fileDetails.markdownContent) {
            sessionStorage.setItem('documentContent', fileDetails.markdownContent);
          }
          policyConversionSource = 'existing';
          // For policy files, use a separate store ID based on category
          policyStoreId = `kb-policy-${orgId?.substring(0, 8)}-${user?.id?.substring(0, 8)}`;
          console.log('✅ Loaded existing policy file details:', {
            markdownUrl: fileDetails.markdownUrl,
            hasContent: !!fileDetails.markdownContent,
            policyStoreId: policyStoreId,
          });
        } else {
          // Fallback to using generated store ID
          policyStoreId = `kb-policy-${orgId?.substring(0, 8)}-${user?.id?.substring(0, 8)}`;
          console.warn('⚠️ Could not fetch details for existing policy file, using generated store ID');
        }
      }

      if (standardFile && selectedStandardFiles.length === 0) {
        console.log('📤 Uploading standard file:', standardFile.name);
        const standardUploadResult = await uploadToRagStore(currentStoreId, standardFile, 'standard', ragService, user?.id, orgId) as any;
        console.log('✅ Standard file uploaded');
        standardStoreId = standardUploadResult?.storeId || currentStoreId;
      } else if (selectedStandardFiles.length > 0) {
        // Fetch file details for existing selected standard file
        console.log('📥 Fetching details for existing standard file:', selectedStandardFiles[0]);
        const standardFileDetails = await getFileDetails(selectedStandardFiles[0], 'standard', user?.id, orgId);
        
        if (standardFileDetails) {
          // For standard files, we use a separate store ID based on category
          standardStoreId = `kb-standard-${orgId?.substring(0, 8)}-${user?.id?.substring(0, 8)}`;
          console.log('✅ Set standard store ID for existing file:', standardStoreId);
        } else {
          // Fallback to using current store but with standard prefix
          standardStoreId = `kb-standard-${orgId?.substring(0, 8)}-${user?.id?.substring(0, 8)}`;
          console.warn('⚠️ Could not fetch details for existing standard file, using generated store ID');
        }
      }

      // Reload files list after upload
      if (policyFile || standardFile) {
        await loadUploadedFiles(currentStoreId);
      }

      // Store audit data in session storage with storeId and file names
      const auditDataWithStore = {
        ...auditData,
        storeId: currentStoreId,
        policyFileName: policyFileName,
        standardFileName: standardFileName,
        conversionSource: policyConversionSource,
      };
      sessionStorage.setItem('auditComparisonData', JSON.stringify(auditDataWithStore));
      sessionStorage.setItem('selectedIndustry', selectedIndustry);
      sessionStorage.setItem('orgId', orgId);
      console.log('🔄 Stored audit data with storeId:', {
        storeId: currentStoreId,
        policyFileName: policyFileName,
        standardFileName: standardFileName,
      });

      // NEW: Create draft policy review record for resume workflow
      try {
        const createResponse = await fetch('/api/revisr/policy-review/create', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-user-id': user?.id || '',
            'x-org-id': orgId,
          },
          body: JSON.stringify({
            policy_name: policyFileName,
            policy_markdown_url: policyMarkdownUrl || null,
            policy_store_id: policyStoreId || currentStoreId,
            standard_store_id: standardStoreId || currentStoreId,
          }),
        });

        if (createResponse.ok) {
          const { id: policyReviewId } = await createResponse.json();
          sessionStorage.setItem('policy_review_id', policyReviewId);
          console.log('✅ Created draft policy review record:', policyReviewId);
        } else {
          console.warn('⚠️ Could not create policy review record, continuing without it');
        }
      } catch (reviewError) {
        console.warn('⚠️ Error creating policy review record:', reviewError);
        // Continue even if this fails - the workflow should still work
      }

      // Navigate directly to ScanAndReview page (skip SelectPolicyDocument)
      navigate('/revisr/scan-review');
    } catch (error) {
      console.error('Error uploading files:', error);
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "Failed to upload files. Please try again.",
        variant: "destructive",
      });
    } finally {
      uploadInProgressRef.current = false;
      setIsUploading(false);
    }
  };

  const renderUploadColumn = (
    title: string,
    auditType: 'oldAudit' | 'newAudit',
    dropzone: any
  ) => {
    const data = auditData[auditType];
    const { getRootProps, getInputProps, isDragActive } = dropzone;
    const isPolicy = auditType === 'oldAudit';
    const selectedExistingFile = isPolicy ? selectedPolicyFile : (selectedStandardFiles.length > 0 ? selectedStandardFiles[0] : null);

    return (
      <div className="space-y-6">
        <div className="text-left">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
        </div>

        {/* File Upload */}
        <div className="space-y-4">

          {!data.file && !selectedExistingFile ? (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 cursor-pointer transition-colors ${isDragActive
                ? "border-[#33b864] bg-[#33b864]/10"
                : "border-gray-300 hover:border-[#33b864]/50"
                }`}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center">
                <Upload className="h-12 w-12 text-[#33b864] mb-4" />
                <p className="text-sm text-gray-600 mb-2">
                  {isDragActive ? "Drop the file here" : "Drag & drop a file here, or click to select"}
                </p>
                <p className="text-xs text-gray-500">
                  Supports PDF, DOC, DOCX, TXT (max 50MB)
                </p>
              </div>
            </div>
          ) : selectedExistingFile ? (
            <div className="border rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileText className="h-8 w-8 text-[#33b864]" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{selectedExistingFile}</p>
                    <p className="text-xs text-gray-500">Existing file in store</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (isPolicy) {
                        setSelectedPolicyFile(null);
                      } else {
                        setSelectedStandardFiles([]);
                      }
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="border rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileText className="h-8 w-8 text-[#33b864]" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{data.file.name}</p>
                    <p className="text-xs text-gray-500">{data.file.size}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePreviewFile(data.file.file)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    Preview Audit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(auditType)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Industry Selection - Hidden but code preserved */}
        <div className="space-y-2 hidden">
          <Label className="text-sm font-medium text-gray-700">Industry</Label>
          <Select
            value={selectedIndustry}
            onValueChange={setSelectedIndustry}
            open={isIndustryOpen}
            onOpenChange={setIsIndustryOpen}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select industry" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <div className="sticky top-0 bg-white p-2 border-b">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search industries..."
                    value={industrySearch}
                    onChange={(e) => setIndustrySearch(e.target.value)}
                    className="pl-8"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
              <div className="max-h-[200px] overflow-y-auto">
                {filteredIndustries.length > 0 ? (
                  filteredIndustries.map((industry) => (
                    <SelectItem key={industry.value} value={industry.value}>
                      {industry.label}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    No industries found
                  </div>
                )}
              </div>
            </SelectContent>
          </Select>
        </div>

        {/* Document Type - Hidden but code preserved */}
        <div className="space-y-2 hidden">
          <Label className="text-sm font-medium text-gray-700">Document Type</Label>
          <Select
            value={data.validationType}
            onValueChange={(value) => updateAuditField(auditType, 'validationType', value)}
            open={isDocumentTypeOpen}
            onOpenChange={setIsDocumentTypeOpen}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select document type" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <div className="sticky top-0 bg-white p-2 border-b">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search document types..."
                    value={documentTypeSearch}
                    onChange={(e) => setDocumentTypeSearch(e.target.value)}
                    className="pl-8"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
              <div className="max-h-[200px] overflow-y-auto">
                {filteredDocumentTypes.length > 0 ? (
                  filteredDocumentTypes.map((docType) => (
                    <SelectItem key={docType.value} value={docType.value}>
                      {docType.label}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    No document types found
                  </div>
                )}
              </div>
            </SelectContent>
          </Select>
        </div>


        {/* Status Indicator */}
        <div className="flex items-center space-x-2 mt-4">
          {(data.file || selectedExistingFile) ? (
            <>
              <CheckCircle className="h-5 w-5 text-[#33b864]" />
              <span className="text-sm text-[#33b864] font-medium">Ready for comparison</span>
            </>
          ) : (
            <>
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <span className="text-sm text-amber-600 font-medium">
                Please upload a document or select an existing file
              </span>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <ScopingAILayout>
      <div className="min-h-screen bg-[#FCFBF4] overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3">Document Upload</h1>
            <p className="text-gray-600 mt-1 text-sm">
              Upload your document for analysis
            </p>
          </div>

          {/* Audit Progress Stepper */}
          {workflowType && (
            <div className="mb-8">
              <Stepper
                currentStep={1}
                steps={
                  workflowType === 'create_policy'
                    ? [
                      { label: "Upload Document", description: "Upload your document for analysis" },
                      { label: "Select Template", description: "Choose formatting template" },
                      { label: "Preview & Export", description: "Finalize and export" },
                    ]
                    : [
                      { label: "Upload Documents", description: "Upload policy and standard documents" },
                      { label: "Scan & Review", description: "AI scans and identifies compliance gaps" },
                      { label: "Edit & Approve", description: "Review and approve changes" },
                      { label: "Preview & Export", description: "Finalize and export" },
                    ]
                }
              />
            </div>
          )}

          {/* Existing Files Section */}
          {storeId && uploadedFiles.length > 0 && (
            <Card className="border-primary/20 mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Select Existing Files ({uploadedFiles.filter(f => f.category === 'policy').length} policy, {uploadedFiles.filter(f => f.category === 'standard').length} standard)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Policy File Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Policy Document:</Label>
                    <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                      {uploadedFiles
                        .filter(file => file.category === 'policy')
                        .map((file) => (
                          <Button
                            key={`policy-${file.name}`}
                            variant={selectedPolicyFile === file.name ? 'default' : 'outline'}
                            size="sm"
                            className="justify-start text-left"
                            onClick={() => handleSelectExistingFile(file.name, 'policy')}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            <span className="truncate">{file.name}</span>
                          </Button>
                        ))}
                      {uploadedFiles.filter(file => file.category === 'policy').length === 0 && (
                        <p className="text-xs text-muted-foreground py-2">No policy documents uploaded yet</p>
                      )}
                    </div>
                    {selectedPolicyFile && (
                      <p className="text-xs text-muted-foreground">
                        Selected: <strong>{selectedPolicyFile}</strong>
                      </p>
                    )}
                  </div>

                  {/* Standard File Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Standard Document:</Label>
                    <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                      {uploadedFiles
                        .filter(file => file.category === 'standard')
                        .map((file) => (
                          <Button
                            key={`standard-${file.name}`}
                            variant={selectedStandardFiles.includes(file.name) ? 'default' : 'outline'}
                            size="sm"
                            className="justify-start text-left"
                            onClick={() => handleSelectExistingFile(file.name, 'standard')}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            <span className="truncate">{file.name}</span>
                          </Button>
                        ))}
                      {uploadedFiles.filter(file => file.category === 'standard').length === 0 && (
                        <p className="text-xs text-muted-foreground py-2">No standard documents uploaded yet</p>
                      )}
                    </div>
                    {selectedStandardFiles.length > 0 && (
                      <div className="text-sm text-muted-foreground mt-2">
                        Selected: <strong>{selectedStandardFiles.join(', ')}</strong>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Divider */}
          {storeId && uploadedFiles.length > 0 && (
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 border-t"></div>
              <span className="text-sm text-muted-foreground">OR</span>
              <div className="flex-1 border-t"></div>
            </div>
          )}

          {/* Upload Section - Two Side Layout */}
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Policy Document Upload */}
              <Card className="p-6 border-[#33b864]/20 shadow-md bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Policy Document
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {renderUploadColumn("Policy Document", "oldAudit", oldAuditDropzone)}
                </CardContent>
              </Card>

              {/* Standard Document Upload */}
              <Card className="p-6 border-[#33b864]/20 shadow-md bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Standard Document
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {renderUploadColumn("Standard Document", "newAudit", newAuditDropzone)}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Continue Button */}
          <div className="flex justify-center items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/revisr/dashboard")}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </Button>

            <Button
              onClick={handleSelectTemplate}
              disabled={!canCompare() || isUploading}
              className="bg-[#33b864] text-white hover:bg-[#33b864]/90 gap-2 px-8 py-3 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Uploading Files...
                </>
              ) : (
                <>
                  <ArrowRight className="h-5 w-5" />
                  Continue to Scan & Review
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewDialog.isOpen} onOpenChange={closePreview}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Preview: {previewDialog.file?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {/* Always show the sample PDF for now */}
            <iframe
              src="/Psycological Health and Wellbeing Policy.pdf"
              className="w-full h-[75vh] border-0"
              title="PDF Preview"
              style={{ border: 'none', display: 'block' }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </ScopingAILayout>
  );
}
